import React, { useRef, useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import BlestFiltersModal from "../components/BlestFiltersModal";
import "../Console.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const IrisStatementOfAccounts = ({ domain }) => {
  const statementRef = useRef();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterTagType, setFilterTagType] = useState("");
  const [filterClient, setFilterClient] = useState("");

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    window.addEventListener("contextmenu", handleContextMenu);
    return () => window.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setErr(null);
      try {
        const response = await fetch("https://api.iriscrypt.com/order", {
          method: "GET",
          headers: { "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43" },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const sorted = [...result].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
        );
        setOrders(sorted);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        setErr("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculatePricing = (order) => {
    if (!order || !order.metadata) {
      return { unitCost: 0, additionalCharge: 0, subTotal: 0, totalAmount: 0 };
    }
    const purpose = order.metadata.purpose?.toLowerCase() || "";
    let unitCost = 0;
    let additionalCharge = 0;
    if (purpose !== "demo") {
      const isCustom = order.metadata.type?.toLowerCase() === "custom";
      unitCost = isCustom ? 5 : 4;
      additionalCharge = isCustom ? 150000 : 0;
    }
    const subTotal = (order.quantity || 0) * unitCost;
    const totalAmount = subTotal + additionalCharge;
    return { unitCost, additionalCharge, subTotal, totalAmount };
  };

  const getClientNames = (order) => {
    if (!order.assignments || !order.assignments.length) return "Unassigned";
    const names = Array.from(
      new Set(order.assignments.map((a) => a.client?.name).filter((n) => !!n)),
    );
    return names.join(", ");
  };

  const allTagTypes = useMemo(() => {
    const types = new Set(orders.map((o) => o.metadata?.type).filter(Boolean));
    return Array.from(types).sort();
  }, [orders]);

  const allClients = useMemo(() => {
    const names = new Set();
    orders.forEach((o) => {
      (o.assignments || []).forEach((a) => {
        if (a.client?.name) names.add(a.client.name);
      });
    });
    return Array.from(names).sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (dateFrom) {
        if (new Date(order.timestamp) < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (new Date(order.timestamp) > toDate) return false;
      }
      if (filterTagType) {
        if (order.metadata?.type?.toLowerCase() !== filterTagType.toLowerCase())
          return false;
      }
      if (filterClient) {
        if (!getClientNames(order).includes(filterClient)) return false;
      }
      return true;
    });
  }, [orders, dateFrom, dateTo, filterTagType, filterClient]);

  const filteredTotal = useMemo(() => {
    return filteredOrders.reduce((sum, order) => {
      const { totalAmount } = calculatePricing(order);
      return sum + totalAmount;
    }, 0);
  }, [filteredOrders]);

  const overallTotal = useMemo(() => {
    return orders.reduce((sum, order) => {
      const { totalAmount } = calculatePricing(order);
      return sum + totalAmount;
    }, 0);
  }, [orders]);

  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const hasFilters = dateFrom || dateTo || filterTagType || filterClient;

  const activeFilterCount = [
    dateFrom,
    dateTo,
    filterTagType,
    filterClient,
  ].filter(Boolean).length;

  const handleApplyFilters = ({
    dateFrom: f,
    dateTo: t,
    filterTagType: tt,
    filterClient: fc,
  }) => {
    setDateFrom(f);
    setDateTo(t);
    setFilterTagType(tt);
    setFilterClient(fc);
  };

  const handleDownload = async () => {
    if (!statementRef.current || !filteredOrders.length) return;
    const canvas = await html2canvas(statementRef.current, {
      scale: 2,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

    let yPos = 0;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, "PNG", 0, yPos, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      yPos -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, yPos, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save("iris-statement-of-accounts.pdf");
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setFilterTagType("");
    setFilterClient("");
  };

  return (
    <>
      <Helmet>
        <title>Statement of Accounts | IRIS</title>
      </Helmet>

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,153,1,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <button
                  onClick={() => window.history.back()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    background: "rgba(255,255,255,0.18)",
                    border: "1.5px solid rgba(255,255,255,0.35)",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "8px 16px",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: "pointer",
                    backdropFilter: "blur(4px)",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="fa-solid fa-arrow-left"
                    style={{ fontSize: 12 }}
                  />
                  Back
                </button>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 4,
                    }}
                  >
                    <i
                      className="fa-solid fa-file-invoice-dollar"
                      style={{
                        fontSize: 22,
                        filter: "drop-shadow(1px 2px 0px #1c1c1c)",
                      }}
                    />
                    <b
                      style={{
                        fontSize: 26,
                        textShadow: "#1c1c1c 1px 2px 0px",
                        lineHeight: 1.1,
                      }}
                    >
                      Statement of Accounts
                    </b>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.85)",
                      textShadow: "#1c1c1c 1px 1px 0px",
                      paddingLeft: 2,
                    }}
                  >
                    IRIS Tag Orders Summary
                  </div>
                </div>
              </div>
              {filteredOrders.length > 0 && (
                <button
                  onClick={handleDownload}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                    border: "none",
                    color: "#c13d00",
                    borderRadius: 30,
                    padding: "10px 22px",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                    boxShadow: "rgba(0,0,0,0.25) 0px 3px 8px",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="fa-solid fa-download"
                    style={{ fontSize: 13 }}
                  />
                  Download PDF
                </button>
              )}
            </div>
          </header>

          {/* Body */}
          <div
            style={{
              padding: "20px 24px",
              backgroundColor: "#f0f2f5",
              minHeight: "calc(100% - 76px)",
            }}
          >
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: 220 }}
              >
                <div
                  className="spinner-border text-secondary"
                  role="status"
                >
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            ) : err ? (
              <div className="alert alert-danger">{err}</div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="row g-3 mb-4">
                  {[
                    {
                      label: "Total Orders",
                      value: hasFilters ? filteredOrders.length : orders.length,
                      sub: hasFilters ? `${orders.length} total` : null,
                    },
                    {
                      label: "Total Tags",
                      value: (hasFilters ? filteredOrders : orders)
                        .reduce((s, o) => s + (o.quantity || 0), 0)
                        .toLocaleString(),
                      sub: hasFilters
                        ? `${orders.reduce((s, o) => s + (o.quantity || 0), 0).toLocaleString()} total`
                        : null,
                    },
                    {
                      label: "Amount (UGX)",
                      value: `${(hasFilters ? filteredTotal : overallTotal).toLocaleString()}`,
                      sub: hasFilters
                        ? `${overallTotal.toLocaleString()} total`
                        : null,
                    },
                    {
                      label: "Clients",
                      value: allClients.length,
                      sub: null,
                    },
                  ].map(({ label, value, sub }) => (
                    <div
                      className="col-6 col-md-3"
                      key={label}
                    >
                      <div
                        style={{
                          background: "white",
                          borderRadius: 10,
                          padding: "16px 20px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                          height: "100%",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "#999",
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            marginBottom: 6,
                          }}
                        >
                          {label}
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: "#1a1a1a",
                            lineHeight: 1,
                          }}
                        >
                          {value}
                        </div>
                        {sub && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#aaa",
                              marginTop: 4,
                            }}
                          >
                            {sub}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Filter trigger row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    marginBottom: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => setShowFiltersModal(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#fff",
                      border: "1.5px solid #e5e7eb",
                      borderRadius: 10,
                      padding: "8px 16px",
                      fontWeight: 700,
                      fontSize: 13,
                      color: "#374151",
                      cursor: "pointer",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <i
                      className="fas fa-sliders"
                      style={{
                        color: activeFilterCount ? "#ff5100" : "#9ca3af",
                        fontSize: 13,
                      }}
                    />
                    Filters
                    {activeFilterCount > 0 && (
                      <span
                        style={{
                          background: "#ff5100",
                          color: "#fff",
                          borderRadius: 9999,
                          fontSize: 11,
                          fontWeight: 800,
                          padding: "1px 7px",
                          lineHeight: 1.6,
                        }}
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  {dateFrom && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(255,81,0,0.08)",
                        border: "1px solid rgba(255,81,0,0.2)",
                        borderRadius: 9999,
                        padding: "4px 10px",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#c13d00",
                      }}
                    >
                      From: {dateFrom}
                      <button
                        onClick={() => setDateFrom("")}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#c13d00",
                          lineHeight: 1,
                          fontSize: 12,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {dateTo && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(255,81,0,0.08)",
                        border: "1px solid rgba(255,81,0,0.2)",
                        borderRadius: 9999,
                        padding: "4px 10px",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#c13d00",
                      }}
                    >
                      To: {dateTo}
                      <button
                        onClick={() => setDateTo("")}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#c13d00",
                          lineHeight: 1,
                          fontSize: 12,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filterTagType && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(255,81,0,0.08)",
                        border: "1px solid rgba(255,81,0,0.2)",
                        borderRadius: 9999,
                        padding: "4px 10px",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#c13d00",
                      }}
                    >
                      Type: {filterTagType}
                      <button
                        onClick={() => setFilterTagType("")}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#c13d00",
                          lineHeight: 1,
                          fontSize: 12,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filterClient && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(255,81,0,0.08)",
                        border: "1px solid rgba(255,81,0,0.2)",
                        borderRadius: 9999,
                        padding: "4px 10px",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#c13d00",
                      }}
                    >
                      Client: {filterClient}
                      <button
                        onClick={() => setFilterClient("")}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#c13d00",
                          lineHeight: 1,
                          fontSize: 12,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {hasFilters && (
                    <button
                      onClick={clearFilters}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc2626",
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "4px 6px",
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Statement document */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {filteredOrders.length === 0 ? (
                    <div
                      style={{
                        padding: "48px 24px",
                        color: "#aaa",
                        textAlign: "center",
                        background: "white",
                        borderRadius: 10,
                        width: "100%",
                        maxWidth: "21cm",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                      }}
                    >
                      <i
                        className="fa-solid fa-file-circle-xmark"
                        style={{
                          fontSize: 40,
                          marginBottom: 12,
                          display: "block",
                        }}
                      />
                      No orders match the current filters.
                    </div>
                  ) : (
                    <div
                      ref={statementRef}
                      style={{
                        width: "21cm",
                        minHeight: "29.7cm",
                        background: "white",
                        padding: "40px",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "11pt",
                        color: "#333",
                        boxShadow: "0 2px 16px rgba(0,0,0,0.11)",
                        borderRadius: 4,
                      }}
                    >
                      {/* Logo & Company */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "30px",
                        }}
                      >
                        <img
                          src="/images/iris_logo_dark.png"
                          alt="IrisCrypt Logo"
                          style={{ height: "150px" }}
                        />
                        <div style={{ textAlign: "right" }}>
                          <b>IrisCrypt Inc.</b>
                          <br />
                          Toronto, ON, Canada
                          <br />
                          <span style={{ fontSize: "10pt", color: "#777" }}>
                            Generated on {formatDate(new Date().toISOString())}
                          </span>
                        </div>
                      </div>

                      {/* Title & Bill To */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "25px",
                        }}
                      >
                        <div>
                          <h4 style={{ marginBottom: "5px" }}>
                            Statement of Accounts
                          </h4>
                          <div style={{ fontSize: "10pt", color: "#555" }}>
                            Summary of IRIS tag orders and charges.
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <b>BILL TO:</b>
                          <br />
                          Bleep Security Tags Limited
                          <br />
                          Anti-Counterfeit Network Africa
                          <br />
                          Plot 40, Nakasero Road, Kampala, Uganda
                        </div>
                      </div>

                      <hr style={{ margin: "10px 0 20px" }} />

                      {/* Summary */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "15px",
                          fontSize: "10pt",
                        }}
                      >
                        <div>
                          <b>Total Orders:</b> {filteredOrders.length}
                          <br />
                          <b>Total Quantity:</b>{" "}
                          {filteredOrders
                            .reduce((sum, o) => sum + (o.quantity || 0), 0)
                            .toLocaleString()}{" "}
                          tags
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <b>Overall Total Amount:</b>{" "}
                          {filteredTotal.toLocaleString()} UGX
                        </div>
                      </div>

                      {/* Details Table */}
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          marginTop: "10px",
                        }}
                      >
                        <thead>
                          <tr>
                            {[
                              "#",
                              "Date",
                              "Tag Type",
                              "Serial Range",
                              "Qty",
                              "Purpose",
                              "Client / Assignment",
                              "Unit Cost (UGX)",
                              "Design (UGX)",
                              "Total (UGX)",
                            ].map((h) => (
                              <th
                                key={h}
                                style={{
                                  border: "1px solid #ddd",
                                  padding: "6px",
                                  backgroundColor: "#f5f5f5",
                                  fontSize: "9pt",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.map((order, index) => {
                            const { unitCost, additionalCharge, totalAmount } =
                              calculatePricing(order);
                            return (
                              <tr key={order._id || index}>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                  }}
                                >
                                  {order.seq ?? index + 1}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                  }}
                                >
                                  {formatDate(order.timestamp)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                  }}
                                >
                                  {order.metadata?.type} (
                                  {order.metadata?.length}
                                  mm)
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                  }}
                                >
                                  {order.minSerial} - {order.maxSerial}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                    textAlign: "right",
                                  }}
                                >
                                  {order.quantity?.toLocaleString()}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                  }}
                                >
                                  {order.metadata?.purpose
                                    ? order.metadata.purpose
                                        .charAt(0)
                                        .toUpperCase() +
                                      order.metadata.purpose.slice(1)
                                    : "-"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                  }}
                                >
                                  {getClientNames(order)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                    textAlign: "right",
                                  }}
                                >
                                  {unitCost.toLocaleString()}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                    textAlign: "right",
                                  }}
                                >
                                  {additionalCharge.toLocaleString()}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "6px",
                                    fontSize: "9pt",
                                    textAlign: "right",
                                  }}
                                >
                                  {totalAmount.toLocaleString()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Footer total */}
                      <div
                        style={{
                          textAlign: "right",
                          marginTop: "20px",
                          fontSize: "11pt",
                        }}
                      >
                        <b>
                          Overall Total Amount: {filteredTotal.toLocaleString()}{" "}
                          UGX
                        </b>
                      </div>

                      <hr style={{ margin: "30px 0" }} />

                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "10pt",
                          color: "#555",
                          marginTop: "20px",
                        }}
                      >
                        Thank you for your continued partnership.
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <BlestFiltersModal
        show={showFiltersModal}
        handleClose={() => setShowFiltersModal(false)}
        dateFrom={dateFrom}
        dateTo={dateTo}
        filterTagType={filterTagType}
        filterClient={filterClient}
        allTagTypes={allTagTypes}
        allClients={allClients}
        isBrandAdmin={false}
        onApply={handleApplyFilters}
        onClear={clearFilters}
      />
    </>
  );
};

export default IrisStatementOfAccounts;
